<script lang="ts" setup>
const props = defineProps<{
  time: number;
}>();

const { t } = useI18n();

const timer = ref(0);
const audio = ref<HTMLAudioElement>();
const timeout = ref<NodeJS.Timeout>();
const isTimerStarted = ref(false);
const wakeLock = ref<WakeLockSentinel>();

const requestWakeLock = async () => {
  navigator.wakeLock?.request?.('screen').then((sentinel) => {
    sentinel.addEventListener('release', () => {
      wakeLock.value = undefined;
    });

    wakeLock.value = sentinel;
  });
};

const releaseWakeLock = async () => {
  await wakeLock.value?.release();
  wakeLock.value = undefined;
};

const startTimer = () => {
  timer.value = props.time;
  isTimerStarted.value = true;
  audio.value = new Audio('/audio/notification.wav');
  requestWakeLock();
};

watch(timer, () => {
  if (timer.value && isTimerStarted.value) {
    clearTimeout(timeout.value);
    timeout.value = setTimeout(() => {
      if (timer.value) {
        timer.value -= 1;
      } else {
        audio.value?.play();

        setTimeout(() => {
          releaseWakeLock();
        }, 5000);
      }
    }, 1000);
  }
});

onBeforeUnmount(() => {
  releaseWakeLock();
});
</script>

<template>
  <Transition mode="out-in" name="fast-fade">
    <Button
      v-if="!isTimerStarted"
      severity="danger"
      icon="pi pi-clock"
      size="large"
      class="!w-16 !h-16 !rounded-full"
      @click="startTimer"
      :aria-label="t('start_timer')"
    />
    <Knob
      v-else
      v-model="timer"
      :max="time"
      readonly
      :stroke-width="6"
      class="!w-16 !h-16 pointer-events-none relative z-1 bg-surface-800 rounded-full"
      :pt="{ svg: 'block w-full h-full' }"
    />
  </Transition>
</template>

<i18n>
en:
  start_timer: Start task timer

ru:
  start_timer: Запустить таймер задания

de:
  start_timer: Task-Timer starten

es:
  start_timer: Iniciar el cronómetro de tareas

fr:
  start_timer: Démarrer le minuteur de tâches
</i18n>
